import { useApolloClient } from '@apollo/client/react';
import { useEffect, useState } from 'react';

import { GET_ACCESS_DIRECTORY } from './operations/index.js';
import type { AccessAdmin, GrantingRoles } from './permissions-context.js';

export type AccessDirectory = {
  roleNames: readonly string[];
  grantingRoles: ReadonlyMap<string, GrantingRoles>;
  accessAdmins: readonly AccessAdmin[];
};

/** Loads once per signed-in user with `client.query`, so the app-wide refetch after every mutation doesn't repeat it. */
export function useAccessDirectory(userId: string | null | undefined): AccessDirectory | undefined {
  const client = useApolloClient();
  const [directory, setDirectory] = useState<{ userId: string; value: AccessDirectory }>();

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    client
      .query({ query: GET_ACCESS_DIRECTORY, fetchPolicy: 'network-only' })
      .then(({ data }) => {
        if (cancelled || !data?.me) return;
        setDirectory({
          userId,
          value: {
            roleNames: data.me.roleNames,
            grantingRoles: new Map(
              data.me.grantingRoles.map(({ permission, roleNames, roleCount }) => [
                permission,
                { roleNames, roleCount },
              ]),
            ),
            accessAdmins: data.accessAdmins,
          },
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [client, userId]);

  return directory && directory.userId === userId ? directory.value : undefined;
}
